import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useRaidFvBoot from '../../hooks/useRaidFvBoot';
import { RAID_FV_BOOT_MAX_MS } from './raidLogoAssets';
import RaidFvBootLoader from './RaidFvBootLoader';
import styles from './RaidFvBootGate.module.css';

const RaidFvBootContext = createContext({
  booting: true,
  reducedMotion: false,
});

const GAUGE_COMPLETE_HOLD_MS = 120;

export function useRaidFvBootContext() {
  return useContext(RaidFvBootContext);
}

export default function RaidFvBootGate({ children }) {
  const { status, reducedMotion } = useRaidFvBoot();
  const [showLoader, setShowLoader] = useState(true);
  const [loaderExiting, setLoaderExiting] = useState(false);
  const [gaugeProgress, setGaugeProgress] = useState(0);

  useEffect(() => {
    if (status === 'ready') {
      return undefined;
    }

    const startedAt = Date.now();
    const updateProgress = () => {
      const elapsed = Date.now() - startedAt;
      const ratio = Math.min(elapsed / RAID_FV_BOOT_MAX_MS, 1);
      // preload 完了前は 90% で止め、ready になったら 100% へ跳ねさせる。
      setGaugeProgress(Math.min(0.9, ratio * 0.9));
    };

    updateProgress();
    const intervalId = window.setInterval(updateProgress, 50);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [status]);

  useEffect(() => {
    if (status !== 'ready') {
      return undefined;
    }

    setGaugeProgress(1);

    const timeoutId = window.setTimeout(() => {
      setLoaderExiting(true);
    }, GAUGE_COMPLETE_HOLD_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [status]);

  const contextValue = useMemo(
    () => ({
      booting: showLoader,
      reducedMotion,
    }),
    [reducedMotion, showLoader]
  );

  return (
    <RaidFvBootContext.Provider value={contextValue}>
      <div
        className={`${styles.content} ${showLoader ? styles.contentHidden : ''}`}
        aria-hidden={showLoader}
      >
        {children}
      </div>
      {showLoader && (
        <RaidFvBootLoader
          exiting={loaderExiting}
          progress={gaugeProgress}
          onExited={() => setShowLoader(false)}
        />
      )}
    </RaidFvBootContext.Provider>
  );
}
